import { getFormSubmitEvent } from '@app/components/common/analytics/analyticsUtils';
import {
  PCC_HEADLESS_BLOCKS_CATEGORY_PAGES,
  PCC_RELEASE_HEADLESS_PDP_FLAG
} from '@app/constants/featureFlags';
import {
  HeaderSearchBarSuggestion,
  HeaderSearchBarSuggestionList,
  HeaderSearchBarSuggestionsColumn,
  HeaderSearchBarSuggestionsContainer,
  HeaderSearchBarSuggestionsSeparator
} from '@cat-ecom/pcc-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useTracking } from 'react-tracking';
import styles from './redesignedSuggestionList.module.scss';
import { getSuggestedDiagrams } from './utils';
import { isEmpty, replaceTokensInString } from '@app/utils';
import { headlessLinks } from '@app/constants/links';
import { DIAGRAMS } from '../constants';

export const RedesignedSuggestionList = ({
  initialSearchValue,
  searchValue,
  isHeadlessSearchEnabled
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();

  const categoryLabel = t('CAT_SHOP_ALL_CATEGORIES');
  const productLabel = t('PRODUCTS');
  const keywordLabel = t('SUGGESTED_KEYWORDS');
  const diagramLabel = t('CAT_PARTS_DIAGRAM');

  const suggestedCategories =
    useSelector(
      s => s.partsFilter.filter?.searchSuggestions?.suggestedCategories
    ) || [];
  const suggestedProducts =
    useSelector(
      s => s.partsFilter.filter?.searchSuggestions?.suggestedProducts
    ) || [];
  const suggestedKeyWords =
    useSelector(
      s => s.partsFilter.filter?.searchSuggestions?.suggestedKeyWords
    ) || [];
  const isHeadlessCategoryEnabled = useSelector(
    s => s.featureFlag[PCC_HEADLESS_BLOCKS_CATEGORY_PAGES]
  );
  const suggestedDiagrams =
    useSelector(
      s => s.partsFilter.filter?.searchSuggestions?.suggestedDiagrams
    ) || [];
  const model = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.model
  );

  const serialNumber = useSelector(
    s => s.myEquipment?.equipments?.selectedEquipment?.serialNumber
  );

  const esiteURL = useSelector(s => s.common.esiteURL);
  const modifiedSuggestedDiagrams = getSuggestedDiagrams(
    suggestedDiagrams,
    esiteURL
  );

  const isPDPHeadlessFeatureFlagEnable =
    useSelector(s => s.featureFlag[PCC_RELEASE_HEADLESS_PDP_FLAG]) ?? false;

  const seoURL = useSelector(s => s.dealer.seoURL);

  const searchedValue = initialSearchValue || searchValue;

  const hasKeyWordsOrCategories =
    !isEmpty(suggestedKeyWords) || !isEmpty(suggestedCategories);

  const hasProductsOrDiagrams =
    !isEmpty(suggestedProducts) || !isEmpty(suggestedDiagrams);

  const hasKeywordsAndCategories =
    !isEmpty(suggestedKeyWords) && !isEmpty(suggestedCategories);

  const hasProductsAndDiagrams =
    !isEmpty(suggestedProducts) && !isEmpty(suggestedDiagrams);

  const basePath = replaceTokensInString(
    headlessLinks.HEADLESS_SEARCH_PAGE,
    esiteURL,
    searchedValue
  );

  const productParams = new URLSearchParams({
    p_page: '1',
    ...(serialNumber && { serialNumber }),
    ...(model && { model, filterByEquipment: 'true' })
  });

  const diagramParams = new URLSearchParams({
    d_page: '1',
    responseFilter: DIAGRAMS,
    ...(serialNumber && { serialNumber }),
    ...(model && { model, filterByEquipment: 'true' })
  });

  const productURL = `${basePath}&${productParams.toString()}`;
  const diagramsURL = `${basePath}&${diagramParams.toString()}`;

  const trackItemSelection = displayName => {
    trackEvent(
      getFormSubmitEvent({
        formName: 'search',
        formContent: searchedValue,
        formContent2: displayName
      })
    );
  };

  suggestedKeyWords.forEach(keyword => {
    keyword.value = isHeadlessSearchEnabled
      ? `${esiteURL}${keyword.value}`
      : keyword.value;
  });

  suggestedProducts.forEach(product => {
    product.value = isPDPHeadlessFeatureFlagEnable
      ? `${esiteURL}/product/${product.partNumber}`
      : product.value;
  });

  return (
    <HeaderSearchBarSuggestionList>
      <HeaderSearchBarSuggestionsContainer
        displayThreeColumns={hasKeyWordsOrCategories}
      >
        <HeaderSearchBarSuggestionsColumn
          displayColumn={hasKeyWordsOrCategories}
          className={styles['column-one']}
        >
          <div className={hasKeywordsAndCategories ? 'pb-md-4' : ''}>
            <HeaderSearchBarSuggestion
              label={keywordLabel}
              items={suggestedKeyWords}
              onItemClick={trackItemSelection}
              highlightText={searchedValue}
              isFederatedSearchEnabled
              listItemClassName={
                styles['redesigned-suggestion-list-item__keywords--padding']
              }
            />
          </div>
          <div
            className={`${styles['redesigned-suggestion-list-item__category--last-item']} text-truncate`}
          >
            <HeaderSearchBarSuggestion
              label={categoryLabel}
              items={suggestedCategories}
              onItemClick={trackItemSelection}
              highlightText={searchedValue}
              isHeadlessCategoryEnabled={isHeadlessCategoryEnabled}
              seoURL={seoURL}
              isFederatedSearchEnabled
              listItemClassName={
                styles['redesigned-suggestion-list-item__category--padding']
              }
            />
          </div>
        </HeaderSearchBarSuggestionsColumn>
        <HeaderSearchBarSuggestionsSeparator
          displaySeparator={hasKeyWordsOrCategories}
        />
        <HeaderSearchBarSuggestionsColumn
          displayColumn={hasProductsOrDiagrams}
          className={hasKeyWordsOrCategories ? styles['column-three'] : ''}
        >
          <div className={hasProductsAndDiagrams ? 'pb-md-4' : ''}>
            <HeaderSearchBarSuggestion
              label={productLabel}
              items={suggestedProducts}
              onItemClick={trackItemSelection}
              highlightText={searchedValue}
              isFederatedSearchEnabled
              displayArrow
              labelProps={{ isStatic: false, href: productURL }}
            />
          </div>
          <div>
            <HeaderSearchBarSuggestion
              label={diagramLabel}
              items={modifiedSuggestedDiagrams}
              onItemClick={trackItemSelection}
              highlightText={searchValue}
              isFederatedSearchEnabled
              listItemClassName={
                styles['redesigned-suggestion-list-item__diagrams--padding']
              }
              displayArrow
              labelProps={{ isStatic: false, href: diagramsURL }}
            />
          </div>
        </HeaderSearchBarSuggestionsColumn>
      </HeaderSearchBarSuggestionsContainer>
    </HeaderSearchBarSuggestionList>
  );
};

RedesignedSuggestionList.propTypes = {
  initialSearchValue: PropTypes.string,
  searchValue: PropTypes.string,
  isHeadlessSearchEnabled: PropTypes.bool
};
